import React, { useEffect, useId, useRef } from 'react';
import { AssetIcon } from '../../shared/assets';
import IconAction from '../../shared/components/IconAction';
import PopUpInfos from '../../shared/components/PopUpInfos';
import { useLinkedEntityCards } from '../../shared/components/linked-entities/LinkedEntityCardHost';
import useTenantNotifications from './useTenantNotifications';
import '../../shared/components/comments/commentThread.css';
import './notifications.css';

const EVENT_ICONS = {
  TASK_ACCEPTED: 'status.completed',
  TASK_REJECTED: 'actions.reject',
  TASK_COMPLETED: 'status.completed',
  DEAL_PROMOTED: 'nav.dealsActive',
  DEAL_COMPLETED: 'nav.dealsCompleted',
  DOCUMENT_STATUS_CHANGED: 'nav.documents',
  CALENDAR_REMINDER: 'nav.calendar',
  PAYMENT_RECEIVED: 'metrics.currency',
};

const dateFormatter = new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function TenantNotifications({ enabled, open, onOpenChange }) {
  const inbox = useTenantNotifications(enabled);
  const { openEntityCard } = useLinkedEntityCards();
  const id = useId();
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const { refresh } = inbox;
  useEffect(() => {
    if (open) { refresh(); panelRef.current?.focus(); }
  }, [open, refresh]);

  function close() { onOpenChange(false); triggerRef.current?.focus(); }
  async function openNotification(notification) {
    if (await inbox.markRead(notification)) {
      onOpenChange(false);
      openEntityCard({ entityType: notification.entityType, entityId: notification.entityId });
    }
  }

  return <div className="tenant-notifications">
    <PopUpInfos label="Notifiche" side="bottom">
      <button ref={triggerRef} type="button" className="tenant-notifications__trigger"
        aria-label={inbox.unreadCount ? `Notifiche: ${inbox.unreadCount} non lette` : 'Notifiche'}
        aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
        onClick={() => onOpenChange(!open)}>
        <AssetIcon name="bell" size={16} />
        {inbox.unreadCount > 0 && <span className="tenant-notifications__badge" aria-hidden="true">{inbox.unreadCount}</span>}
      </button>
    </PopUpInfos>
    {open && <section ref={panelRef} id={id} role="dialog" aria-label="Notifiche" tabIndex={-1}
      className="tenant-notifications__panel" onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); close(); }
      }}>
      <header className="tenant-notifications__header">
        <div><h2>Notifiche</h2><p>{inbox.unreadCount ? `${inbox.unreadCount} non lette` : 'Tutte lette'}</p></div>
        <div className="tenant-notifications__actions">
          <IconAction iconKey="status.completed" label="Segna tutte come lette" onClick={inbox.markAllRead}
            disabled={inbox.busy || !inbox.unreadCount || !inbox.items.length} />
          <IconAction iconKey="actions.close" label="Chiudi notifiche" onClick={close} />
        </div>
      </header>
      {inbox.error && <p role="alert" className="wt-inline-error tenant-notifications__message">{inbox.error}</p>}
      <div className="tenant-notifications__list" aria-busy={inbox.loading}>
        {!inbox.items.length && <p className="tenant-notifications__empty" role="status">{inbox.loading ? 'Caricamento notifiche…' : inbox.error ? 'Le notifiche non sono al momento disponibili.' : 'Non hai ancora notifiche.'}</p>}
        <ul>{inbox.items.map(notification => <li key={notification.id}>
          <button type="button" className={`tenant-notifications__item${notification.readAt ? '' : ' is-unread'}`}
            disabled={inbox.busy} onClick={() => openNotification(notification)}>
            <span className="comment-thread__avatar tenant-notifications__avatar" aria-hidden="true">
              <AssetIcon size={16} name={notification.eventType === 'CALENDAR_REMINDER' ? 'nav.calendar' : 'user'} />
            </span>
            <span className="comment-item__card tenant-notifications__copy">
              <span className="comment-item__header tenant-notifications__author">
                <strong>{notification.actorName || (notification.eventType === 'CALENDAR_REMINDER' ? 'Calendario' : 'Utente')}
                  {!notification.readAt && <span className="tenant-notifications__unread-dot" aria-label="Non letta" />}
                </strong>
                <time dateTime={notification.createdAt}>{dateFormatter.format(new Date(notification.createdAt))}</time>
              </span>
              <span className="tenant-notifications__event">
                <AssetIcon size={16} name={EVENT_ICONS[notification.eventType] || 'bell'} />
                <span>{notification.title}</span>
              </span>
              <span className="tenant-notifications__body">{notification.message || notification.body}</span>
            </span>
          </button>
        </li>)}</ul>
      </div>
      <footer className="tenant-notifications__footer">
        {inbox.nextBeforeId && <IconAction iconKey="actions.arrowDown" label="Mostra notifiche precedenti" disabled={inbox.loading || inbox.busy} onClick={inbox.loadMore} />}
        <IconAction iconKey="actions.reset" label="Aggiorna notifiche" disabled={inbox.loading || inbox.busy} onClick={() => inbox.refresh()} />
      </footer>
    </section>}
  </div>;
}
